// SPDX-License-Identifier: AGPL-3.0-or-later

import {GuildBoostBadgeIcon} from '@app/features/guild/components/GuildBoostBadgeIcon';
import styles from '@app/features/guild/components/GuildBoostIntegratedProgress.module.css';
import {
	GUILD_BOOST_GOAL_PILL_DESCRIPTOR,
	GUILD_BOOST_LEVEL_BADGE_DESCRIPTOR,
	GUILD_BOOST_MAX_LEVEL_DESCRIPTOR,
	GUILD_BOOST_PROGRESS_FRACTION_DESCRIPTOR,
} from '@app/features/guild/utils/GuildBoostMessageDescriptors';
import {useLingui} from '@lingui/react/macro';
import {CaretRightIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type React from 'react';

export interface GuildBoostIntegratedProgressModel {
	readonly ratio: number;
	readonly level: number;
	readonly count: number;
	readonly nextRequired: number | null;
	readonly isMaxLevel: boolean;
}

interface GuildBoostIntegratedProgressProps extends GuildBoostIntegratedProgressModel {
	readonly variant?: 'compact' | 'relaxed';
	readonly showChevron?: boolean;
	readonly className?: string;
}

export function GuildBoostIntegratedProgress({
	ratio,
	level,
	count,
	nextRequired,
	isMaxLevel,
	variant = 'compact',
	showChevron = false,
	className,
}: GuildBoostIntegratedProgressProps) {
	const {i18n} = useLingui();
	const isCompact = variant === 'compact';

	const levelLabel =
		level > 0 ? i18n._(GUILD_BOOST_LEVEL_BADGE_DESCRIPTOR, {level}) : i18n._(GUILD_BOOST_GOAL_PILL_DESCRIPTOR);

	// At max level the boost count is noise; only the achievement is shown.
	const metaLabel = isMaxLevel
		? i18n._(GUILD_BOOST_MAX_LEVEL_DESCRIPTOR)
		: i18n._(GUILD_BOOST_PROGRESS_FRACTION_DESCRIPTOR, {
				current: count,
				required: nextRequired ?? count,
			});

	return (
		<div
			className={clsx(styles.shell, isCompact && styles.shellCompact, className)}
			data-flx="guild.guild-boost-integrated-progress.shell"
		>
			<div className={styles.backdrop} aria-hidden />
			<div
				className={styles.fill}
				style={{width: `${Math.round(Math.min(1, Math.max(0, ratio)) * 1000) / 10}%`}}
				aria-hidden
			/>
			<div className={clsx(styles.content, isCompact && styles.contentCompact)}>
				<span className={clsx(styles.levelBadge, !isCompact && styles.levelBadgeRelaxed)}>
					<GuildBoostBadgeIcon className={styles.badgeIcon} size={12} />
					<span className={styles.badgeLabel}>{levelLabel}</span>
				</span>
				<span className={clsx(styles.meta, !isCompact && styles.metaRelaxed)}>
					<span className={styles.metaText}>{metaLabel}</span>
					{showChevron ? <CaretRightIcon weight="bold" className={styles.chevron} aria-hidden /> : null}
				</span>
			</div>
		</div>
	);
}
